import * as React from 'react';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroll-component';
import PropTypes from 'prop-types';
import { Input, Loader } from 'oxyrion-ui/lib';
import { rem } from 'polished';
import { __ } from '../../Utils';

const SelectWrapper = styled.div`
  width: 100%;
  color: black;
  .Select-control {
    border: ${({ theme }) => `solid ${rem(1)} ${theme.separatorColor}`};
  }
`;

const SelectItem = styled.li`
  display: flex;
  justify-content: flex-start;
  font-size: 14px;
  color: #252525;
  align-items: center;
  height: ${rem(32)};
  background: ${props => (props.selected ? '#eee' : 'white')};
  padding: ${rem(4)} ${rem(8)};
  cursor: pointer;
  &:hover {
    background: #eee;
  }
`;

const MenuWrapper = styled.ul`
  margin: 0px;
  padding: 0px;
`;

const ScrollWrapper = styled.div`
  max-height: ${rem(400)};
  opacity: ${props => (props.display ? '1' : '0')};
  height: ${props => (props.display ? 'auto' : '0px')};
  overflow: auto;
  position: absolute;
  width: ${rem(332)};
  background: white;
  z-index: 1000;
  box-shadow: 0 ${rem(3)} ${rem(5)} 0 rgba(0, 0, 0, 0.16);
`;

const LoaderWrapper = styled.div`
  height: ${rem(42)};
  padding: ${rem(16)};
  display: flex;
  justify-content: center;
  background: white;
`;

const StyledInput = styled(Input)`
  width: ${rem(360)};
  > input {
    cursor: default;
    &:focus {
      cursor: default;
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: ${rem(320)};
`;

class SearchableLazyLoadingSelect extends React.PureComponent {
  constructor(props) {
    super(props);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.state = {
      showItems: false,
      searchText: '',
      cursor: -1,
      items: [],
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    const { openOnLoad, hideItems } = this.props;

    if (openOnLoad) {
      this.setState(
        {
          showItems: !hideItems,
          offset: 0,
          items: [],
        },
        () => {
          this.fetchData();
        },
      );
    }
  }

  componentWillReceiveProps(nextProps) {
    const { keyProps } = this.props;

    const { items: prevItems } = this.state;

    if (keyProps !== nextProps.keyProps) {
      this.setState(
        {
          offset: 0,
          items: [],
          searchText: '',
        },
        () => {
          this.fetchData(prevItems.length === 0);
        },
      );
    }

    if (!this.props.defaultSearchText && nextProps.defaultSearchText) {
      this.setState({
        searchText: nextProps.defaultSearchText,
      });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  onSelect(e, index) {
    const { onSelect } = this.props;

    this.setState(
      {
        showItems: false,
        searchText: e.fullName,
        cursor: index,
      },
      () => onSelect(e),
    );
  }

  setWrapperRef(node) {
    this.wrapperRef = node;
  }

  handleClickOutside(event) {
    if (
      this.wrapperRef &&
      !this.wrapperRef.contains(event.target) &&
      this.state.showItems
    ) {
      this.setState({
        showItems: false,
      });
    }
  }

  async handleSearch(q) {
    const self = this;

    if (self.state.typingTimeout) {
      clearTimeout(self.state.typingTimeout);
    }

    self.setState({
      searchText: q,
      typing: false,
      innerError: false,
      typingTimeout: setTimeout(() => this.fetchData(true), 1200),
    });
  }

  async fetchData(loading = true) {
    const { fetchData, hideItems, limit } = this.props;
    const { searchText } = this.state;

    this.setState({
      showItems: !hideItems,
      loading,
    });

    try {
      if (!fetchData) {
        throw new Error(__('Funkcia fetchData nie je implementovaná'));
      }

      const res = await fetchData(searchText, limit, 0);

      if (!res.options) {
        this.setState({
          items: [],
          hasMoreItems: false,
          loading: false,
          innerError: false,
        });
      } else {
        this.setState({
          items: res.options,
          hasMoreItems: res.options.length === limit,
          offset: res.next_offset,
          loading: false,
          showItems: !hideItems,
          innerError: false,
          // cursor: res.options.length > 0 ? 0 : -1,
        });
      }
    } catch (e) {
      this.setState({
        loading: false,
        innerError: true,
      });
    }
  }

  handleKeyDown(e) {
    const { cursor, items, searchText } = this.state;
    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38 && cursor > 0) {
      this.setState(prevState => ({
        cursor: prevState.cursor - 1,
      }));
    } else if (e.keyCode === 40 && cursor < items.length - 1) {
      this.setState(prevState => ({
        cursor: prevState.cursor + 1,
      }));
    } else if (e.key === 'Enter') {
      const item = items.find(
        i => i.fullName.toUpperCase() === searchText.toUpperCase(),
      );

      if (item && cursor === -1) {
        this.onSelect(item, cursor);
      } else if (items[cursor]) {
        this.onSelect(items[cursor], cursor);
      }
    }
  }

  fetchDataSync() {
    const { fetchData, limit } = this.props;
    const { offset, searchText, items } = this.state;

    if (!fetchData) {
      throw new Error(__('Funkcia fetchData nie je implementovaná'));
    }

    try {
      fetchData(searchText, limit, offset || 0).then(res => {
        if (!res.options) {
          this.setState({
            items: [],
            hasMoreItems: false,
          });
        } else {
          this.setState({
            items: items.concat(res.options),
            hasMoreItems: res.options.length === limit,
            offset: res.next_offset,
          });
        }
      });
    } catch (e) {
      this.setState({
        items: [],
        hasMoreItems: false,
      });
    }
  }

  render() {
    const {
      placeholder,
      disabled,
      keyProps,
      inputRef,
      error,
      treshold,
      width,
    } = this.props;

    const { showItems, items, searchText, loading, innerError } = this.state;

    return (
      <SelectWrapper key={keyProps}>
        <div ref={n => this.setWrapperRef(n)}>
          <InputWrapper>
            <StyledInput
              onKeyDown={this.handleKeyDown}
              key={keyProps}
              disabled={disabled}
              inputRef={inputRef}
              style={{
                maxWidth: width ? rem(width) : rem(340),
                width: width ? '100%' : '87%',
                marginBottom: rem(2),
              }}
              onClick={e => {
                if (items.length === 0) {
                  this.setState({
                    showItems: true,
                    loading: true,
                  });
                  this.handleSearch(e.target.value);
                } else if (!showItems && (items.length || loading)) {
                  this.setState({
                    showItems: true,
                  });
                }
              }}
              error={error || innerError}
              onChange={e => this.handleSearch(e.target.value)}
              placeholder={placeholder}
              value={searchText}
            />
          </InputWrapper>

          <ScrollWrapper id="scrollableDiv" display={showItems && !disabled}>
            <InfiniteScroll
              dataLength={this.state.items.length}
              next={() => {
                if (loading) {
                  this.setState({
                    loading: false,
                  });
                }
                return this.fetchDataSync();
              }}
              height={!error ? 400 : 0}
              scrollThreshold={treshold}
              // style={{ display: 'flex', flexDirection: 'column-reverse' }} //To put endMessage and loader to the top.
              hasMore={this.state.hasMoreItems}
              loader={
                <LoaderWrapper>
                  <Loader size="xl" />
                </LoaderWrapper>
              }
              // scrollableTarget="scrollableDiv"
            >
              <MenuWrapper>
                {!items.length && loading && (
                  <LoaderWrapper>
                    <Loader size="xl" />
                  </LoaderWrapper>
                )}
                {items
                  // .slice(this.state.selectedItemIndex, items.length)
                  .map((item, index) => (
                    <SelectItem
                      key={item.value}
                      selected={this.state.cursor === index}
                      onClick={() => this.onSelect(item, index)}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
              </MenuWrapper>
            </InfiniteScroll>
          </ScrollWrapper>
        </div>
      </SelectWrapper>
    );
  }
}

SearchableLazyLoadingSelect.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.shape({}),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  openOnLoad: PropTypes.bool,
  keyProps: PropTypes.string,
  inputRef: PropTypes.func,
  fetchData: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  treshold: PropTypes.number,
  limit: PropTypes.number,
  hideItems: PropTypes.bool,
};

SearchableLazyLoadingSelect.defaultProps = {
  value: {},
  placeholder: '',
  disabled: false,
  error: false,
  openOnLoad: false,
  keyProps: '',
  inputRef: () => {},
  hideItems: false,
  limit: 30,
  treshold: 0.8, // load next value in 80% scroll
};

export default SearchableLazyLoadingSelect;



// WEBPACK FOOTER //
// ./src/Components/SearchableLazyLoadingSelect/index.js